import { useEffect } from "react";
import { useLang } from "../../context/LanguageContext";
import {
  X,
  MapPin,
  Sailboat,
  Coffee,
  Utensils,
  TreePalm,
  Church,
  Home,
  TreePine,
  Landmark,
  Mountain,
  Footprints,
  ExternalLink,
} from "lucide-react";
import { BEACHES, CAFES, TAVERNS, OLD_VILLAGE } from "../../data/sithoniaGuide";

const ICONS = { Church, Home, TreePine, Landmark, Mountain, Footprints };

const TOPIC_META = {
  beaches: { Icon: Sailboat, color: "from-sky-100 to-blue-50", titleEl: "Παραλίες της Σιθωνίας", titleEn: "Sithonia Beaches", overlineEl: "Οδηγός παραλιών", overlineEn: "Beach guide" },
  cafes: { Icon: Coffee, color: "from-amber-50 to-orange-50", titleEl: "Καφέ & Beach Bars", titleEn: "Cafés & Beach Bars", overlineEl: "Πού θα πιείτε καφέ", overlineEn: "Coffee & drinks" },
  taverns: { Icon: Utensils, color: "from-rose-50 to-amber-50", titleEl: "Ταβέρνες & Εστιατόρια", titleEn: "Taverns & Restaurants", overlineEl: "Παραδοσιακή κουζίνα", overlineEn: "Traditional cuisine" },
  oldVillage: { Icon: TreePalm, color: "from-stone-100 to-amber-50", titleEl: "Παλιό Χωριό Νικήτης", titleEn: "Old Village of Nikiti", overlineEl: "Πολιτισμός & ιστορία", overlineEn: "Culture & history" },
};

export default function SithoniaGuideDialog({ topic, open, onClose }) {
  const { lang } = useLang();

  useEffect(() => {
    if (open) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      return () => {
        document.body.style.overflow = prev;
      };
    }
  }, [open]);

  if (!open || !topic) return null;
  const meta = TOPIC_META[topic];
  const { Icon } = meta;

  return (
    <div
      data-testid="sithonia-guide-dialog"
      className="fixed inset-0 z-[100] bg-black/60 backdrop-blur-sm overflow-y-auto"
    >
      <div
        className="min-h-screen flex items-start justify-center p-2 sm:p-6"
        onMouseDown={(e) => {
          if (e.target === e.currentTarget) onClose();
        }}
      >
        <div
          className="w-full max-w-6xl bg-white rounded-2xl sm:rounded-3xl overflow-hidden shadow-2xl my-2 sm:my-6"
          onMouseDown={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className={`relative bg-gradient-to-br ${meta.color} px-5 sm:px-10 py-8 sm:py-12 border-b border-[#E9E2D3]`}>
            <button
              data-testid="guide-dialog-close"
              onClick={onClose}
              className="absolute top-4 right-4 sm:top-6 sm:right-6 w-10 h-10 rounded-full bg-white/90 backdrop-blur flex items-center justify-center hover:bg-white shadow-md transition"
              aria-label="Close"
            >
              <X className="w-5 h-5 text-[#1A2B4C]" />
            </button>
            <div className="flex items-center gap-3 mb-3">
              <span className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-white/80 backdrop-blur flex items-center justify-center">
                <Icon className="w-5 h-5 sm:w-6 sm:h-6 text-[#1A2B4C]" />
              </span>
              <p className="uppercase text-[10px] sm:text-xs tracking-[0.25em] text-[#6B705C]">
                {lang === "el" ? meta.overlineEl : meta.overlineEn}
              </p>
            </div>
            <h2 className="font-serif text-3xl sm:text-5xl text-[#1A2B4C] tracking-tight leading-[1.05]">
              {lang === "el" ? meta.titleEl : meta.titleEn}
            </h2>
          </div>

          {/* Content */}
          <div className="px-4 sm:px-10 py-8 sm:py-12">
            {topic === "beaches" && <BeachesContent lang={lang} />}
            {topic === "cafes" && <CafesContent lang={lang} />}
            {topic === "taverns" && <TavernsContent lang={lang} />}
            {topic === "oldVillage" && <OldVillageContent lang={lang} />}
          </div>
        </div>
      </div>
    </div>
  );
}

function BeachesContent({ lang }) {
  return (
    <div data-testid="beaches-content" className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 sm:gap-6">
      {BEACHES.map((b) => (
        <article
          key={b.id}
          data-testid={`beach-card-${b.id}`}
          className="group bg-[#F5F2EB] rounded-2xl overflow-hidden border border-[#E9E2D3] hover:shadow-xl hover:-translate-y-1 transition-all duration-500"
        >
          <div className="relative h-48 overflow-hidden bg-gradient-to-br from-sky-100 to-blue-50">
            <img
              src={b.img}
              alt={lang === "el" ? b.name_el : b.name_en}
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
              loading="lazy"
              onError={(e) => {
                e.currentTarget.style.display = "none";
              }}
            />
            <Sailboat className="absolute inset-0 m-auto w-12 h-12 text-[#1A2B4C]/30 -z-0" />
            <span className="absolute top-3 left-3 bg-white/95 backdrop-blur px-3 py-1 rounded-full text-[10px] tracking-widest uppercase text-[#1A2B4C] z-10">
              {b.area}
            </span>
          </div>
          <div className="p-5 sm:p-6">
            <h3 className="font-serif text-xl sm:text-2xl text-[#1A2B4C] mb-2">
              {lang === "el" ? b.name_el : b.name_en}
            </h3>
            <p className="text-gray-700 text-sm leading-relaxed mb-4">
              {lang === "el" ? b.desc_el : b.desc_en}
            </p>
            <div className="flex flex-wrap gap-1.5 mb-4">
              {b.tags.map((tag) => (
                <span
                  key={tag}
                  className="text-[10px] uppercase tracking-wider bg-white border border-[#E9E2D3] text-[#6B705C] px-2.5 py-1 rounded-full"
                >
                  {tag}
                </span>
              ))}
            </div>
            <a
              href={b.maps}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1.5 text-xs text-[#1A2B4C] hover:underline"
            >
              <MapPin className="w-3.5 h-3.5" />
              {lang === "el" ? "Δες στο χάρτη" : "View on map"}
              <ExternalLink className="w-3 h-3" />
            </a>
          </div>
        </article>
      ))}
    </div>
  );
}

function CafesContent({ lang }) {
  return (
    <div data-testid="cafes-content" className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 sm:gap-6">
      {CAFES.map((c) => (
        <article
          key={c.id}
          data-testid={`cafe-card-${c.id}`}
          className="group bg-[#F5F2EB] rounded-2xl overflow-hidden border border-[#E9E2D3] hover:shadow-xl hover:-translate-y-1 transition-all duration-500"
        >
          <div className="relative h-44 overflow-hidden bg-gradient-to-br from-amber-50 to-orange-50">
            <img
              src={c.img}
              alt={c.name}
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
              loading="lazy"
              onError={(e) => { e.currentTarget.style.display = "none"; }}
            />
            <Coffee className="absolute inset-0 m-auto w-10 h-10 text-[#1A2B4C]/25 -z-0" />
            <span className="absolute bottom-3 left-3 bg-[#1A2B4C]/90 backdrop-blur text-white px-3 py-1 rounded-full text-[10px] tracking-widest uppercase z-10">
              {lang === "el" ? c.style_el : c.style_en}
            </span>
          </div>
          <div className="p-5 sm:p-6">
            <p className="uppercase text-[10px] tracking-widest text-[#6B705C] mb-1">{c.area}</p>
            <h3 className="font-serif text-xl sm:text-2xl text-[#1A2B4C] mb-2">{c.name}</h3>
            <p className="text-gray-700 text-sm leading-relaxed mb-3">
              {lang === "el" ? c.desc_el : c.desc_en}
            </p>
            <p className="text-[11px] uppercase tracking-wider text-[#6B705C]">
              <span className="text-[#1A2B4C] font-medium">
                {lang === "el" ? "Ιδανικό για" : "Ideal for"}:
              </span>{" "}
              {c.ideal}
            </p>
          </div>
        </article>
      ))}
    </div>
  );
}

function TavernsContent({ lang }) {
  return (
    <div data-testid="taverns-content" className="grid sm:grid-cols-2 gap-5 sm:gap-6">
      {TAVERNS.map((t) => (
        <article
          key={t.id}
          data-testid={`tavern-card-${t.id}`}
          className="group bg-[#F5F2EB] rounded-2xl overflow-hidden border border-[#E9E2D3] hover:shadow-xl transition-all duration-500"
        >
          <div className="grid sm:grid-cols-5 gap-0">
            <div className="sm:col-span-2 relative h-48 sm:h-full overflow-hidden bg-gradient-to-br from-rose-50 to-amber-50">
              <img
                src={t.img}
                alt={t.name}
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                loading="lazy"
                onError={(e) => { e.currentTarget.style.display = "none"; }}
              />
              <Utensils className="absolute inset-0 m-auto w-10 h-10 text-[#1A2B4C]/25 -z-0" />
            </div>
            <div className="sm:col-span-3 p-5 sm:p-6">
              <p className="uppercase text-[10px] tracking-widest text-[#6B705C] mb-1">{t.area}</p>
              <h3 className="font-serif text-xl sm:text-2xl text-[#1A2B4C] mb-2">{t.name}</h3>
              <p className="text-gray-700 text-sm leading-relaxed mb-3">
                {lang === "el" ? t.desc_el : t.desc_en}
              </p>
              <div className="border-t border-[#E9E2D3] pt-3 mt-3">
                <p className="uppercase text-[10px] tracking-widest text-[#6B705C] mb-1">
                  {lang === "el" ? "Σπεσιαλιτέ" : "Specialties"}
                </p>
                <p className="text-sm text-[#1A2B4C] italic mb-3">
                  {lang === "el" ? t.specialty_el : t.specialty_en}
                </p>
                <p className="text-[11px] uppercase tracking-wider text-[#6B705C]">{t.atmosphere}</p>
              </div>
            </div>
          </div>
        </article>
      ))}
    </div>
  );
}

function OldVillageContent({ lang }) {
  return (
    <div data-testid="old-village-content" className="space-y-12 sm:space-y-16">
      {/* Hero image */}
      <div className="relative -mx-4 sm:-mx-10 -mt-8 sm:-mt-12 mb-2 overflow-hidden">
        <img
          src={OLD_VILLAGE.hero_img}
          alt="Nikiti Old Village"
          className="w-full h-52 sm:h-80 object-cover"
          loading="lazy"
          onError={(e) => { e.currentTarget.style.display = "none"; }}
        />
      </div>

      {/* Intro */}
      <div className="max-w-3xl">
        <p className="font-serif text-lg sm:text-2xl text-[#1A2B4C] leading-relaxed italic">
          "{lang === "el" ? OLD_VILLAGE.intro_el : OLD_VILLAGE.intro_en}"
        </p>
      </div>

      {/* Gallery */}
      {OLD_VILLAGE.gallery && OLD_VILLAGE.gallery.length > 0 && (
        <div>
          <p className="uppercase text-[10px] sm:text-xs tracking-[0.25em] text-[#6B705C] mb-5 sm:mb-6">
            {lang === "el" ? "Στιγμές" : "Glimpses"}
          </p>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {OLD_VILLAGE.gallery.map((src, i) => (
              <div key={i} className="aspect-[4/3] rounded-xl overflow-hidden bg-gradient-to-br from-stone-100 to-amber-50">
                <img
                  src={src}
                  alt={`Old village ${i + 1}`}
                  className="w-full h-full object-cover hover:scale-105 transition-transform duration-700"
                  loading="lazy"
                  onError={(e) => { e.currentTarget.style.display = "none"; }}
                />
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Timeline */}
      <div>
        <p className="uppercase text-[10px] sm:text-xs tracking-[0.25em] text-[#6B705C] mb-5 sm:mb-6">
          {lang === "el" ? "Ιστορική γραμμή" : "Timeline"}
        </p>
        <div className="relative pl-6 sm:pl-8 border-l-2 border-[#E9E2D3] space-y-6 sm:space-y-8">
          {OLD_VILLAGE.timeline.map((t, i) => (
            <div key={i} className="relative">
              <span className="absolute -left-[34px] sm:-left-[42px] top-1 w-4 h-4 rounded-full bg-[#1A2B4C] ring-4 ring-[#F5F2EB]" />
              <p className="font-serif text-lg sm:text-xl text-[#1A2B4C]">{t.year}</p>
              <p className="text-gray-700 text-sm sm:text-base leading-relaxed mt-1">
                {lang === "el" ? t.el : t.en}
              </p>
            </div>
          ))}
        </div>
      </div>

      {/* Highlights */}
      <div>
        <p className="uppercase text-[10px] sm:text-xs tracking-[0.25em] text-[#6B705C] mb-5 sm:mb-6">
          {lang === "el" ? "Τι θα δείτε" : "What you'll see"}
        </p>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5">
          {OLD_VILLAGE.highlights.map((h) => {
            const HighlightIcon = ICONS[h.icon] || Landmark;
            return (
              <div
                key={h.key}
                data-testid={`old-village-${h.key}`}
                className="group bg-[#F5F2EB] rounded-2xl p-5 sm:p-6 border border-[#E9E2D3] hover:shadow-xl hover:-translate-y-1 transition-all duration-500"
              >
                <span className="inline-flex w-11 h-11 rounded-full bg-white items-center justify-center mb-4 group-hover:bg-[#1A2B4C] transition-colors">
                  <HighlightIcon className="w-5 h-5 text-[#6B705C] group-hover:text-white transition-colors" />
                </span>
                <h4 className="font-serif text-lg sm:text-xl text-[#1A2B4C] mb-2">
                  {lang === "el" ? h.title_el : h.title_en}
                </h4>
                <p className="text-sm text-gray-700 leading-relaxed">
                  {lang === "el" ? h.desc_el : h.desc_en}
                </p>
              </div>
            );
          })}
        </div>
      </div>

      {/* Evening walk callout */}
      <div className="bg-gradient-to-br from-[#1A2B4C] to-[#0F1B33] text-white rounded-2xl sm:rounded-3xl p-6 sm:p-10">
        <p className="uppercase text-[10px] sm:text-xs tracking-[0.25em] text-white/60 mb-3">
          {lang === "el" ? "Πρόταση" : "Suggestion"}
        </p>
        <h4 className="font-serif text-2xl sm:text-3xl mb-3">
          {lang === "el"
            ? "Ένας βραδινός περίπατος στο Παλιό Χωριό"
            : "An evening walk through the Old Village"}
        </h4>
        <p className="text-white/80 text-sm sm:text-base leading-relaxed max-w-2xl">
          {lang === "el"
            ? "Ξεκινήστε από την κεντρική πλατεία την ώρα του ηλιοβασιλέματος. Ακολουθήστε τα καλντερίμια προς την Αγία Παρασκευή, σταματήστε για κρασί σε ένα από τα ταβερνάκια και επιστρέψτε από τον λόφο Τσούκα για τη θέα στον κόλπο."
            : "Start at the central square at sunset. Follow the cobbled lanes toward Agia Paraskevi, pause for wine at a small taverna and return via Tsouka Hill for the gulf panorama."}
        </p>
      </div>
    </div>
  );
}
